import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Input, Label, Modal } from './ui';
import { imagesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { ImageMeta } from '../types/api';

/**
 * RenameImageModal — small modal for renaming a single image's
 * filename. Lock-gated server-side; this modal is only mounted by
 * the canvas overlay when the project is unlocked.
 *
 * Extension is locked to whatever the image was uploaded with —
 * customer only edits the part before the dot. Prevents the DB
 * filename from drifting away from the S3 object's actual byte
 * format (e.g. renaming "img.jpg" to "img.gif" would mis-advertise
 * a JPEG on download and break the customer's tools).
 */
interface RenameImageModalProps {
    open: boolean;
    image: ImageMeta | null;
    orgId?: string;
    isDarkMode?: boolean;
    onClose: () => void;
    onRenamed: (updated: ImageMeta) => void;
}

function splitExt(filename: string): { base: string; ext: string } {
    const m = filename.match(/^(.*)(\.[^.\\/]+)$/);
    if (m) return { base: m[1], ext: m[2] };
    return { base: filename, ext: '' };
}

export default function RenameImageModal({
    open,
    image,
    orgId,
    isDarkMode = false,
    onClose,
    onRenamed,
}: RenameImageModalProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const original = useMemo(() => (image ? splitExt(image.filename) : { base: '', ext: '' }), [image]);
    const [base, setBase] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && image) {
            setBase(original.base);
            setError(null);
            setSubmitting(false);
        }
    }, [open, image, original.base]);

    // Customer commonly pastes the full filename ("img.jpg") into a
    // basename-only field — silently strip the trailing locked
    // extension so we don't produce "img.jpg.jpg".
    const cleanBase = useMemo(() => {
        if (!original.ext) return base;
        const tail = original.ext.toLowerCase();
        if (base.toLowerCase().endsWith(tail) && base.length > tail.length) {
            return base.slice(0, base.length - tail.length);
        }
        return base;
    }, [base, original.ext]);

    const fullName = cleanBase + original.ext;

    function handleClose() {
        if (submitting) return;
        setError(null);
        onClose();
    }

    function validate(): boolean {
        const trimmed = cleanBase.trim();
        if (!trimmed) {
            setError(tCommon('required_field', { defaultValue: 'Required' }));
            return false;
        }
        if (trimmed !== cleanBase) {
            setError('Filename has leading/trailing whitespace.');
            return false;
        }
        if (fullName.length > 255) {
            setError('Filename too long (max 255 characters).');
            return false;
        }
        if (/[\/\\]/.test(cleanBase)) {
            setError('Filename cannot contain path separators (/ or \\).');
            return false;
        }
        if (image && fullName === image.filename) {
            setError('New name is the same as the current name.');
            return false;
        }
        setError(null);
        return true;
    }

    async function handleSave() {
        if (!image) return;
        if (!validate()) return;
        setSubmitting(true);
        try {
            const updated = await imagesApi.rename(image.id, fullName, orgId);
            toast.success('Image renamed');
            onRenamed(updated);
            onClose();
        } catch (err) {
            if (err instanceof APIClientError) {
                const code = err.code ?? '';
                if (code === 'invalid_filename') {
                    setError(err.body.message ?? 'Invalid filename.');
                } else if (code === 'project_locked') {
                    setError('Project is locked — unlock to rename.');
                } else {
                    setError(err.body.message ?? tErrors('generic'));
                }
            } else {
                setError(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    // Reserve right-padding for the extension suffix so customer text
    // never collides with the grey ".jpg" / ".png" overlay.
    const suffixPad = original.ext ? `${original.ext.length * 7 + 14}px` : undefined;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Rename image"
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={handleClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => void handleSave()}
                        disabled={submitting}
                    >
                        {submitting ? tCommon('submitting', { defaultValue: 'Saving…' }) : tCommon('save', { defaultValue: 'Save' })}
                    </Button>
                </>
            }
        >
            <Label isDarkMode={isDarkMode} required>
                New filename
            </Label>
            <div className="relative">
                <Input
                    isDarkMode={isDarkMode}
                    value={base}
                    onChange={(e) => setBase(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleSave();
                    }}
                    autoFocus
                    error={!!error}
                    maxLength={255 - original.ext.length}
                    style={suffixPad ? { paddingRight: suffixPad } : undefined}
                />
                {original.ext && (
                    <span
                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-[13px] pointer-events-none select-none ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}
                    >
                        {original.ext}
                    </span>
                )}
            </div>
            {original.ext && (
                <p className={`text-[11px] mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Extension <span className="font-mono">{original.ext}</span> is locked — it must match the uploaded file.
                </p>
            )}
            <p className={`text-[11px] mt-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Will save as <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{fullName || '—'}</span>
                {cleanBase !== base && (
                    <span className={`ml-2 italic ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                        (extension you typed was removed)
                    </span>
                )}
            </p>
            {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </Modal>
    );
}
